import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';

import { useUpdateGdprClauseValidationSchema } from './schema/update-gdpr-clause-validation-schema';

import { GdprClauseType, GdprLanguage } from '@/features/settings/api/types/gdpr';
import { useCreateGDPRClause } from '@/features/settings/hooks/mutation/use-create-gdpr-clause';
import { useUpdateGDPRClause } from '@/features/settings/hooks/mutation/use-update-gdpr-clause';

interface Props extends DialogActions {
  selectedClauseLanguage: GdprLanguage;
  selectedGdprClause: string;
  selectedGdprClauseType: GdprClauseType;
  selectedGdprClauseId: number;
}

interface FormValues {
  gdprClause: string;
  language: GdprLanguage;
  type: GdprClauseType;
}

const defaultValues: FormValues = {
  gdprClause: '',
  language: GdprLanguage.ENGLISH,
  type: GdprClauseType.NEW_USER,
};

export const UpdateGdprClauseDialog = ({
  selectedGdprClause,
  selectedGdprClauseType,
  selectedClauseLanguage,
  selectedGdprClauseId,
  open,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['validation', 'settings']);

  const { mutate: createGdprClause, isPending: isPendingCreateGdprClause } =
    useCreateGDPRClause(onCancel);

  const { mutate: updateGdprClause, isPending: isPendingUpdateGdprClause } =
    useUpdateGDPRClause(onCancel);

  const onConfirm = (
    gdprClause: string,
    language: GdprLanguage,
    type: GdprClauseType,
    gdprClauseId?: number,
  ) => {
    if (gdprClauseId) {
      updateGdprClause({ gdprClauseId, gdprClause });
    } else {
      createGdprClause({ language, gdprClause, type });
    }
  };

  const validationSchema = useUpdateGdprClauseValidationSchema();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    onConfirm(data.gdprClause, data.language, data.type, selectedGdprClauseId);
  };

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
    if (open) {
      reset({
        gdprClause: selectedGdprClause,
        language: selectedClauseLanguage,
        type: selectedGdprClauseType,
      });
    }
  }, [open, selectedGdprClauseId]);

  return (
    <Dialog
      open={open}
      onConfirm={handleSubmit(onSubmit)}
      onCancel={onCancel}
      isConfirmButtonDisabled={!isDirty || !isValid}
      isConfirmButtonSubmitting={isPendingCreateGdprClause || isPendingUpdateGdprClause}
      variant="info"
      title={selectedGdprClauseId ? t('settings:gdpr.editTitle') : t('settings:gdpr.addTitle')}>
      <Controller
        control={control}
        name="gdprClause"
        render={({ field, fieldState: { error } }) => (
          <InputField
            {...field}
            fullWidth
            sx={{ my: 1 }}
            name="gdprClause"
            label={t('settings:gdpr.title')}
            required
            multiline
            error={!!error?.message}
            helperText={error?.message}
          />
        )}
      />
    </Dialog>
  );
};
